import { useEffect, useId, useRef, useState } from 'react';
import { useLocale } from '../../../i18n';
import {
  readVenueAvailability,
  type VenueAvailability as Availability,
  type VenueBookingTerms,
  type VenueCourt,
  type VenueFreeSlot,
} from '../../../lib/api/venues';
import { BigButton, Card, EmptyState, SectionTitle } from '../mobileUi';

// ─────────────────────────────────────────────────────────────────────
// WHAT IS FREE AT A VENUE (#833 slices 2 and 3b).
//
// Explore → Centre → AVAILABLE COURTS → Booking. This is the third step, for
// anybody signed in: the owner's 2026-10-16 decision makes membership a price,
// never access, so a player with no club is shown the same free times a
// member is.
//
// THE PRICE IS THE SERVER'S (#833 slice 3b). Since slice 3a the read carries
// the caller's terms (`booking: { tier, offered, reason? }`) and each slot's
// price at that tier — the figure the booking command would store. This screen
// shows that figure and nothing else: no price is ever worked out here, and
// where the server sent none (booking not offered to this person) none is
// shown. The tier is labelled («Gjestepris» / «Medlemspris») so a guest's
// price never reads as a member's.
//
// PICKING IS THE PAGE'S DECISION. `MobileVenue` says whether this person books
// HERE (`pickable`: somebody who is not a member, where the venue's door takes
// them). A member books through the member booking they already have, so for
// them the list stays a list. The pick goes up to the page, which owns the
// booking — this file never books.
//
// DATES ARE THE SERVER'S. It answers which days it will speak about, in the
// venue's own time, so this screen never computes a `YYYY-MM-DD` — it only
// formats one for display, from its parts (never through UTC).
// ─────────────────────────────────────────────────────────────────────

/** One free time, picked to book: the server's slot, on the day and length it was read for. */
export interface VenueSlotPick {
  date: string;
  durationMin: number;
  slot: VenueFreeSlot;
}

export interface VenueAvailabilityProps {
  slug: string;
  /** The profile's courts, to name a court number the way the venue does. */
  courts: VenueCourt[];
  /** Injected in tests; the real one is `GET /venues/:slug/availability`. */
  load?: typeof readVenueAvailability;
  /**
   * The caller's terms, each time the server answers (#833 slice 3b) — the way
   * on depends on them. Null: the venue has no calendar to show.
   */
  onTerms?: (terms: VenueBookingTerms | null) => void;
  /** This person books here: free times become choices (when the server offers booking). */
  pickable?: boolean;
  /** The time picked, or null when nothing is (a change of day or length clears it). */
  onPick?: (pick: VenueSlotPick | null) => void;
  /**
   * The start time the PAGE holds as picked — controlled, so a pick the page
   * drops (a refusal that says the list moved) is never still marked here.
   */
  picked?: string | null;
  /**
   * Bumped by the page to read the SAME day again — after a booking, or a
   * refusal that says it moved. The pick is the page's to clear then.
   */
  refresh?: number;
}

type State =
  { state: 'loading' } | { state: 'ok'; availability: Availability | null } | { state: 'failed' };

/** `YYYY-MM-DD` as a LOCAL date, for display only. */
export const localDay = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
};

export function VenueAvailability({
  slug,
  courts,
  load = readVenueAvailability,
  onTerms,
  pickable = false,
  onPick,
  refresh = 0,
  picked = null,
}: VenueAvailabilityProps) {
  const { t, formatDate } = useLocale();
  const daysLabel = useId();
  const lengthsLabel = useId();
  // null asks for the venue's today; the server says which day that is.
  const [date, setDate] = useState<string | null>(null);
  // null asks for the venue's shortest length; the server says which that is.
  const [length, setLength] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<State>({ state: 'loading' });
  // The strips stay while the next answer loads, so they do not jump.
  const [days, setDays] = useState<string[]>([]);
  const [lengths, setLengths] = useState<number[]>([]);
  // Read through refs: a parent re-rendering with new callbacks must not re-read.
  const termsRef = useRef(onTerms);
  termsRef.current = onTerms;
  const pickRef = useRef(onPick);
  pickRef.current = onPick;

  // What the current pick was made on: a new day or length is a new list, and
  // whatever was picked from the old one goes. A `refresh` of the SAME day is
  // not — the page asked for it (a booking just made, say) and owns the pick.
  const pickedOn = useRef<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setResult({ state: 'loading' });
    const on = `${date ?? ''}|${length ?? ''}`;
    if (pickedOn.current !== on) {
      pickedOn.current = on;
      pickRef.current?.(null);
    }
    void load(slug, date ?? undefined, controller.signal, length ?? undefined).then((outcome) => {
      if (outcome.status === 'aborted') return;
      if (outcome.status === 'ok') {
        setResult({ state: 'ok', availability: outcome.availability });
        if (outcome.availability) {
          setDays(outcome.availability.dates);
          setLengths(outcome.availability.durations);
        }
        termsRef.current?.(outcome.availability ? outcome.availability.booking : null);
      } else setResult({ state: 'failed' });
    });
    return () => controller.abort();
  }, [slug, date, length, load, attempt, refresh]);

  const shown = result.state === 'ok' ? result.availability : null;
  // A venue the server says has no calendar has nothing to show here at all.
  if (result.state === 'ok' && shown === null) return null;

  const selected = shown?.date ?? date ?? days[0] ?? null;
  const selectedLength = shown?.durationMin ?? length ?? lengths[0] ?? null;
  const courtName = (n: number) =>
    courts.find((c) => c.number === n)?.name ?? t('mobile.venue.courtNumber', { number: n });
  // A CHOICE ONLY WHERE THE SERVER SAYS THIS PERSON MAY BOOK — never on the
  // page's word alone.
  const choosing = pickable && shown?.booking.offered === true;
  const priced = shown?.booking.offered === true && shown.slots.some((s) => s.priceNok !== null);
  const price = (nok: number) =>
    nok === 0 ? t('mobile.activities.free') : t('mobile.venue.priceNok', { amount: String(nok) });

  const choose = (s: VenueFreeSlot) => {
    if (!shown) return;
    const again = picked === s.startTime;
    onPick?.(again ? null : { date: shown.date, durationMin: shown.durationMin, slot: s });
  };

  const chip = (active: boolean) => ({
    background: active ? '#0f1611' : '#fff',
    color: active ? '#fff' : '#0f1611',
    border: `1px solid ${active ? '#0f1611' : '#e7eae3'}`,
  });

  return (
    <div>
      <SectionTitle>{t('mobile.venue.freeTimes')}</SectionTitle>

      {days.length > 0 && (
        <div
          role="group"
          aria-labelledby={daysLabel}
          className="flex gap-1 overflow-x-auto -mx-4 px-4 pb-2 scrollbar-thin"
        >
          <span id={daysLabel} className="sr-only">
            {t('mobile.venue.chooseDay')}
          </span>
          {days.map((d, i) => {
            const day = localDay(d);
            const active = d === selected;
            const weekday =
              i === 0
                ? t('mobile.venue.today')
                : formatDate(day, { weekday: 'short' }).replace('.', '');
            return (
              <button
                key={d}
                type="button"
                onClick={() => setDate(d)}
                aria-pressed={active}
                aria-label={formatDate(day, { weekday: 'long', day: 'numeric', month: 'long' })}
                className="flex-shrink-0 flex flex-col items-center rounded-xl px-2 py-1.5"
                style={{ minWidth: 48, ...chip(active) }}
              >
                <span className="text-[11px] font-semibold">{weekday}</span>
                <span className="text-[15px] font-semibold tabular-nums">{day.getDate()}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* THE LENGTH (#833 slice 3b) — the venue's own lengths, from the server.
          Each slot's price is for the length asked, because a longer booking can
          cross into another zone; so a new length is a new read, never a sum. */}
      {lengths.length > 1 && (
        <div role="group" aria-labelledby={lengthsLabel} className="flex gap-1.5 pb-2">
          <span id={lengthsLabel} className="sr-only">
            {t('mobile.book.pickLength')}
          </span>
          {lengths.map((m) => {
            const active = m === selectedLength;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setLength(m)}
                aria-pressed={active}
                className="rounded-full px-3 py-1 text-[12px] font-semibold"
                style={chip(active)}
              >
                {t('mobile.venue.minutes', { minutes: m })}
              </button>
            );
          })}
        </div>
      )}

      {result.state === 'loading' && <EmptyState title={t('mobile.venue.loading')} />}
      {result.state === 'failed' && (
        <div className="space-y-2">
          <EmptyState title={t('mobile.venue.availabilityFailed')} />
          <BigButton variant="ghost" onClick={() => setAttempt((n) => n + 1)}>
            {t('mobile.venue.retry')}
          </BigButton>
        </div>
      )}
      {shown && shown.slots.length === 0 && <EmptyState title={t('mobile.venue.noFreeTimes')} />}
      {shown && shown.slots.length > 0 && (
        <>
          <div className="flex items-baseline justify-between gap-2 px-1 mb-1.5">
            <p className="text-[12px] text-ink-2">
              {t('mobile.venue.freeTimesFor', { minutes: shown.durationMin })}
            </p>
            {/* WHOSE PRICE THIS IS — the server's tier, so a guest's price never
                reads as a member's. Only where there are prices to label. */}
            {priced && (
              <span
                className="font-mono text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded flex-shrink-0"
                style={{ background: '#eef0ea', color: '#0f1611' }}
                data-testid="venue-price-tier"
              >
                {shown.booking.tier === 'member'
                  ? t('mobile.venue.memberPrice')
                  : t('mobile.venue.guestPrice')}
              </span>
            )}
          </div>
          <ul className="space-y-1.5">
            {shown.slots.map((s) => {
              const body = (
                <div className="flex items-baseline gap-3">
                  <span className="font-mono font-bold text-[14px] text-ink tabular-nums flex-shrink-0">
                    {s.startTime}–{s.endTime}
                  </span>
                  <span className="text-[13px] text-ink-2 min-w-0 flex-1">
                    {t('mobile.venue.freeCourts', {
                      courts: s.courts.map(courtName).join(', '),
                    })}
                  </span>
                  {s.priceNok !== null && (
                    <span className="font-display font-semibold text-[14px] text-ink tabular-nums flex-shrink-0">
                      {price(s.priceNok)}
                    </span>
                  )}
                </div>
              );
              if (!choosing) {
                return (
                  <li key={s.startTime}>
                    <Card>{body}</Card>
                  </li>
                );
              }
              const active = picked === s.startTime;
              return (
                <li key={s.startTime}>
                  <button
                    type="button"
                    onClick={() => choose(s)}
                    aria-pressed={active}
                    className="block w-full text-left rounded-2xl px-4 py-3.5 active:scale-[0.99] transition-transform"
                    style={{
                      background: '#fff',
                      border: `${active ? 2 : 1}px solid ${active ? '#0f1611' : '#ecefe8'}`,
                      boxShadow: '0 1px 2px rgba(15,22,17,0.04)',
                    }}
                  >
                    {body}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
